import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Users, UserPlus, Truck, CalendarOff, MapPinOff, Package, Upload, LogOut, Wheat, Menu, X, CreditCard, Receipt, KeyRound, Building2, FlaskConical, CalendarPlus, History } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'leads', label: 'Leads', icon: UserPlus },
  { id: 'subscribers', label: 'Subscribers', icon: Users },
  { id: 'samples', label: 'Sample Orders', icon: FlaskConical },
  { id: 'plans', label: 'Subscription Plans', icon: CreditCard },
  { id: 'apartments', label: 'Apartments', icon: Building2 },
  { id: 'payments', label: 'Payments', icon: Receipt },
  { id: 'deliveries', label: "Today's Deliveries", icon: Package },
  { id: 'next-day', label: "Next Day Prep", icon: CalendarPlus },
  { id: 'delivery-history', label: 'Delivery History', icon: History },
  { id: 'skips', label: 'Skips & OOT', icon: CalendarOff },
  { id: 'delivery-boys', label: 'Delivery Boys', icon: Truck },
  { id: 'import-export', label: 'Import / Export', icon: Upload },
];

export default function Sidebar({ activePanel, setActivePanel }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredNav = user?.role === 'delivery_boy'
    ? NAV_ITEMS.filter(n => n.id === 'deliveries')
    : NAV_ITEMS;

  return (
    <>
      {/* Mobile toggle */}
      <button
        data-testid="mobile-menu-toggle"
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg"
        style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="w-5 h-5" style={{ color: '#C8A96E' }} /> : <Menu className="w-5 h-5" style={{ color: '#C8A96E' }} />}
      </button>

      {/* Overlay */}
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-full z-40 flex flex-col transition-transform duration-300 md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: '260px', background: '#161311', borderRight: '1px solid #2A241F' }}
      >
        <div className="p-5 flex items-center gap-3" style={{ borderBottom: '1px solid #2A241F' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,169,110,0.1)' }}>
            <Wheat className="w-5 h-5" style={{ color: '#C8A96E' }} />
          </div>
          <div>
            <h2 className="font-bold text-base" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>GrabMillet</h2>
            <p className="text-xs" style={{ color: '#78716C' }}>CRM</p>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {filteredNav.map(item => {
            const isActive = activePanel === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => { setActivePanel(item.id); setMobileOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  background: isActive ? 'rgba(200,169,110,0.1)' : 'transparent',
                  color: isActive ? '#C8A96E' : '#A8A29E',
                  borderRight: isActive ? '2px solid #C8A96E' : '2px solid transparent',
                }}
              >
                <Icon className="w-4.5 h-4.5" style={{ strokeWidth: 1.5 }} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4" style={{ borderTop: '1px solid #2A241F' }}>
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(200,169,110,0.2)', color: '#C8A96E' }}>
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: '#F5F5F4' }}>{user?.name || 'User'}</p>
              <p className="text-xs truncate" style={{ color: '#78716C' }}>{user?.role === 'admin' ? 'Admin' : 'Delivery Boy'}</p>
            </div>
          </div>
          <button
            data-testid="change-password-button"
            onClick={() => { setActivePanel('change-password'); setMobileOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 mb-1"
            style={{ color: '#A8A29E' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2A241F'; e.currentTarget.style.color = '#C8A96E'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A8A29E'; }}
          >
            <KeyRound className="w-4 h-4" style={{ strokeWidth: 1.5 }} /> Change Password
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200"
            style={{ color: '#A8A29E' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2A241F'; e.currentTarget.style.color = '#F87171'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A8A29E'; }}
          >
            <LogOut className="w-4 h-4" style={{ strokeWidth: 1.5 }} /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
